<template>
  <v-container>
    <h1>Tableau d'analyse</h1>
    <p>
      Selectionnez la date de début et de fin pour analyser le classement sur la période.
      Vous pouvez aussi utiliser les boutons rapides pour séléctionner la plage qui vous intéresse.
      Cliquez sur les boutons d'actions pour effectuer une recherche sur les plateformes de média ou
      récupérer l'historique de classement d'une musique.
    </p>
    <ul class="mb-4">
      <li>Nombre de classements : {{ datesCount }}</li>
      <li>Premier classement : {{ firstDate.toLocaleDateString() }}</li>
      <li>Dernier classement : {{ lastDate.toLocaleDateString() }}</li>
    </ul>
    <v-divider></v-divider>
    <v-container fluid>
      <v-row justify="space-between">
        <v-col cols="12" sm="6">
          <v-menu
            v-model="menuDateStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Date de début"
                prepend-icon="mdi-calendar"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              @input="menuDateStart = false"
              no-title
              :events="allDatesString"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            v-model="menuDateEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="Date de fin"
                prepend-icon="mdi-calendar"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              @input="menuDateEnd = false"
              no-title
              :events="allDatesString"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn block small outlined @click="quickPeriod()">
            Ce mois
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block small outlined @click="quickPeriod('lastMonth')">
            Mois dernier
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block small outlined @click="quickPeriod('last3Months')">
            3 Mois
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block small outlined @click="quickPeriod('last6Months')">
            6 Mois
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block small outlined @click="quickPeriod('last12Months')">
            1 an
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block small outlined @click="quickPeriod('last24Months')">
            2 ans
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-data-table
        dense
        multi-sort
        :loading="loading"
        :headers="headers"
        :items="ranking"
        :items-per-page="20"
        class="elevation-1"
        :footer-props="{ 'items-per-page-options': [10, 20, 40, 100, -1] }"
      >
        <template v-slot:item.index="{ item }">
          {{ ranking.indexOf(item) + 1 }}
        </template>
        <template v-slot:item.score="{ item }">
          <v-chip small :color="getColor(item.score)" dark>{{ item.score }}</v-chip>
        </template>
        <template v-slot:item.averageRank="{ item }">
          {{ (40 - item.score / item.weeks).toFixed(1) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            color="error"
            :href="'https://www.youtube.com/results?search_query=' + encodeURI(item.artist + '-' + item.title)"
            target="_blank"
          >
            <v-icon>mdi-youtube</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="primary"
            :href="'https://www.deezer.com/search/' + encodeURI(item.artist + '-' + item.title)"
            target="_blank"
          >
            <v-icon>mdi-music-note</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
export default {
  data: () => ({
    menuDateStart: false,
    menuDateEnd: false,
    startDate: null,
    endDate: null,
    minScore: 0,
    maxScore: 1,
    headers: [
      { text: 'Top', value: 'index' },
      { text: 'Titre', align: 'start', value: 'title' },
      { text: 'Artiste', align: 'start', value: 'artist' },
      { text: 'Score', value: 'score', align: 'center' },
      { text: 'Semaines', value: 'weeks', align: 'center' },
      { text: 'Meilleur top', value: 'bestRanking', align: 'center' },
      { text: 'Top moyen', value: 'averageRank', align: 'center' },
      { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: 100 }
    ]
  }),
  created () {
    this.quickPeriod('last12Months')
  },
  computed: {
    ...mapGetters(['firstDate', 'lastDate', 'allDatesString', 'datesCount']),
    ...mapState(['ranking', 'loading']),
    startEndDate () {
      return `${this.startDate}|${this.endDate}`
    }
  },
  methods: {
    ...mapActions(['loadRanking']),
    quickPeriod (period) {
      const date = new Date()
      let startingDate
      let endingDate

      switch (period) {
        case 'last24Months':
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() - 24, 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 0))
          break
        case 'last12Months':
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() - 12, 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 0))
          break
        case 'last6Months':
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() - 6, 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 0))
          break
        case 'last3Months':
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() - 3, 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 0))
          break
        case 'lastMonth':
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() - 1, 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 0))
          break
        default:
          startingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1))
          endingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() + 1, 0))
      }

      this.startDate = startingDate.toISOString().substring(0, 10)
      this.endDate = endingDate.toISOString().substring(0, 10)
    },
    requestRanking () {
      const start = new Date(this.startDate)
      const end = new Date(this.endDate)
      this.songRankingResult = this.loadRanking({ start, end })
      if (this.songRankingResult.length > 0) {
        this.maxScore = this.songRankingResult.reduce((previous, current) => current.score > previous.score ? current : previous).score
        this.minScore = this.songRankingResult.reduce((previous, current) => current.score < previous.score ? current : previous).score
      } else {
        this.maxScore = 1
        this.minScore = 0
      }
    },
    getColor (score) {
      const scorePercent = (score - this.minScore) / (this.maxScore - this.minScore)
      if (scorePercent > 0.67) return 'red'
      else if (scorePercent > 0.33) return 'orange'
      else return 'green'
    }
  },
  watch: {
    startEndDate () {
      this.requestRanking()
    }
  }
}
</script>
